import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import salesforceLogo from '../../../Assets/salesforce_logo.svg';
import { SalesforceSelection } from '../hooks/useFocusMode';

interface SalesforcePanelProps {
  isOpen: boolean;
  // × / backdrop / Escape / Cancel button — all discard the in-progress
  // pick and close without touching the committed selection.
  onCancel: () => void;
  // Submit button — commits the staged pick (parent closes the modal too).
  onSubmit: (selection: SalesforceSelection) => void;

  loading: boolean;
  error: string;
  // Raw key -> value options as returned by the API (e.g. {project: "opportunity"}).
  options: Record<string, string>;
  committedSelection: SalesforceSelection;

  searchValue: string;
  onSearchChange: (value: string) => void;
}

// Staged pick, local to this modal until Submit. savedValue is only set when
// re-seeded from a committed pick — the table value it already carries.
type Staged =
  | { type: 'none' }
  | { type: 'all' }
  | { type: 'item'; key: string; displayName: string; savedValue?: string | null };

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const toStaged = (sel: SalesforceSelection): Staged => {
  if (sel.type === 'all') return { type: 'all' };
  if (sel.type === 'item' && sel.id) {
    return { type: 'item', key: sel.id, displayName: sel.name ?? capitalize(sel.id), savedValue: sel.value };
  }
  return { type: 'none' };
};

const SalesforcePanel: React.FC<SalesforcePanelProps> = ({
  isOpen,
  onCancel,
  onSubmit,
  loading,
  error,
  options,
  committedSelection,
  searchValue,
  onSearchChange,
}) => {
  const [staged, setStaged] = useState<Staged>(() => toStaged(committedSelection));

  // SalesforcePanel itself never unmounts (Modal just renders null while
  // closed), so re-seed the staged pick from whatever's actually committed
  // every time the modal opens — otherwise a discarded (Cancel'd) pick from
  // a prior open would still be sitting in local state next time it opens.
  useEffect(() => {
    if (isOpen) setStaged(toStaged(committedSelection));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const filteredKeys = Object.keys(options).filter((key) => {
    if (!searchValue.trim()) return true;
    const q = searchValue.trim().toLowerCase();
    return key.toLowerCase().includes(q) || options[key].toLowerCase().includes(q);
  });

  const handleSubmit = () => {
    if (staged.type === 'none') return;
    if (staged.type === 'all') {
      onSubmit({ type: 'all', id: null, name: null });
    } else {
      // The table value goes with the pick (see SalesforceSelection.value).
      // The loaded list is the fresher source; the saved value covers the
      // list failing to load when re-submitting an existing pick.
      onSubmit({
        type: 'item',
        id: staged.key,
        name: staged.displayName,
        value: options[staged.key] ?? staged.savedValue ?? null,
      });
    }
  };

  const summaryText =
    staged.type === 'none' ? 'No selection yet' :
    staged.type === 'all'  ? 'Salesforce: Use Tables available' :
    `1 table selected · ${staged.displayName}`;

  return (
    <Modal isOpen={isOpen} onClose={onCancel} ariaLabel="Select a Salesforce table" maxWidth={600}>
      <div className="salesforce-modal">
        {/* ── Header ── */}
        <div className="salesforce-modal-header">
          <img src={salesforceLogo} alt="" className="salesforce-modal-header-icon" aria-hidden="true" />
          <div className="salesforce-modal-header-text">
            <div className="salesforce-modal-title">Select a Salesforce table</div>
            <div className="salesforce-modal-subtitle">Pick a table to load into this chat, or use all of Salesforce</div>
          </div>
          <button
            type="button"
            className="salesforce-modal-close"
            onClick={onCancel}
            aria-label="Close"
            title="Close"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>

        {/* ── Search ── */}
        <div className="salesforce-modal-search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search tables"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* ── Salesforce: Use Tables available ── */}
        <button
          type="button"
          className={`salesforce-use-all ${staged.type === 'all' ? 'selected' : ''}`}
          onClick={() => setStaged({ type: 'all' })}
        >
          <span className={`salesforce-radio ${staged.type === 'all' ? 'checked' : ''}`} aria-hidden="true" />
          <span className="salesforce-use-all-text">
            <span className="salesforce-use-all-title">Salesforce: Use Tables available</span>
            <span className="salesforce-use-all-desc">Prompt across every table you have access to — no specific table required</span>
          </span>
        </button>

        <div className="salesforce-modal-section-label">OR SELECT A SPECIFIC TABLE</div>

        {/* ── List ── */}
        {error && <div className="focus-error">{error}</div>}
        <div className="salesforce-modal-list">
          {loading && <div className="focus-loading"><i className="fas fa-spinner fa-spin" /> Loading…</div>}
          {!loading && filteredKeys.length === 0 && (
            <div className="focus-empty">No Salesforce options available.</div>
          )}
          {!loading && filteredKeys.map((key) => {
            const displayName = capitalize(key);
            const isSelected = staged.type === 'item' && staged.key === key;
            return (
              <div key={key} className={`salesforce-row ${isSelected ? 'selected' : ''}`}>
                <button
                  type="button"
                  className="salesforce-row-radio-btn"
                  aria-label={`Select ${displayName}`}
                  onClick={() => setStaged({ type: 'item', key, displayName })}
                >
                  <span className={`salesforce-radio ${isSelected ? 'checked' : ''}`} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="salesforce-row-body"
                  onClick={() => setStaged({ type: 'item', key, displayName })}
                >
                  <img src={salesforceLogo} alt="" className="salesforce-row-icon" aria-hidden="true" />
                  <span className="salesforce-row-text">
                    <span className="salesforce-row-name">{displayName}</span>
                  </span>
                </button>
              </div>
            );
          })}
        </div>

        {/* ── Footer ── */}
        <div className="salesforce-modal-footer">
          <span className="salesforce-footer-summary">{summaryText}</span>
          <div className="salesforce-footer-actions">
            <button type="button" className="salesforce-btn-cancel" onClick={onCancel}>Cancel</button>
            <button
              type="button"
              className="salesforce-btn-submit"
              onClick={handleSubmit}
              disabled={staged.type === 'none'}
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SalesforcePanel;
